import StatusPopover from '../../../components/v2/StatusPopover.jsx';
import { Link } from 'react-router-dom';
import FilterPopover from '../../../components/v2/FilterPopover.jsx';
import { campusLocalToMs } from '../../../libs.jsx';
import { relativeTimeFromCampusLocal } from '../../../utils/campusTime.js';
import { skipsStaleCheck, showsMuted, MUTED_BADGE } from '../../../utils/deviceStatus.js';
import AlertBellPopover from '../../../components/v2/AlertBellPopover.jsx';
import { getDeviceProfileLink } from '../../../utils/deviceLinks.js';
import RowActionsMenu from '../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../components/v2/SortableTh.jsx';

/* Manufacturers on the Nurse Call tab whose devices never heartbeat: last_active
   never advances, so the stale test would permanently show them yellow and send
   staff to test working hardware. They report Active whenever they are assigned. */
const STALE_EXEMPT_MFG = [7];

function fullDateTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
}

/**
 * Column definitions for the legacy devices table (9 columns).
 */
export const COLUMN_DEFS = [
  { key: 'device_name', label: 'Name', sortable: true, filterable: true },
  { key: 'inovonics_model_name', label: 'Model', sortable: true, filterable: true },
  { key: 'manufacture_uuid', label: 'Serial Number', sortable: true, filterable: true },
  { key: 'residents', label: 'Residents', sortable: false, filterable: true, filterKey: 'resident_name' },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'descriptions', label: 'Location in Area', sortable: true, filterable: true },
  { key: 'last_alarm', label: 'Last Alarm', sortable: true, filterable: false },
  { key: 'last_active', label: 'Last Active', sortable: true, filterable: false },
  { key: 'is_active', label: 'Active Status', sortable: true, filterable: true },
];

/**
 * Column definitions for the cloud devices table (12 columns).
 * Adds Device Health columns (Battery, Tamper, Offline Status) after area_name.
 */
export const CLOUD_COLUMN_DEFS = [
  { key: 'device_name', label: 'Name', sortable: true, filterable: true },
  { key: 'inovonics_model_name', label: 'Model', sortable: true, filterable: true },
  { key: 'manufacture_uuid', label: 'Serial Number', sortable: true, filterable: true },
  { key: 'residents', label: 'Residents', sortable: false, filterable: true, filterKey: 'resident_name' },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'descriptions', label: 'Location in Area', sortable: true, filterable: true },
  { key: 'low_battery', label: 'Battery', sortable: true, filterable: true, filterKey: 'low_battery_text' },
  { key: 'tamper', label: 'Tamper', sortable: true, filterable: true, filterKey: 'tamper_text' },
  { key: 'offline', label: 'Offline Status', sortable: true, filterable: true, filterKey: 'offline_text' },
  { key: 'last_alarm', label: 'Last Alarm', sortable: true, filterable: false },
  { key: 'last_active', label: 'Last Active', sortable: true, filterable: false },
  { key: 'is_active', label: 'Active Status', sortable: true, filterable: true },
];

/**
 * DevicesTable — full data table for devices with sorting, filtering, sticky header,
 * row selection, row actions, and consistent row heights.
 */
const DevicesTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onUnassignDevice,
  onDeleteDevice,
  editRights,
  deleteRights,
  columnDefs,
  tzOffset = 0,
  /* Map<deviceIdString, alert[]> from alertsByDevice(). A Map rather than a filter
     callback so the table does one pass over the alerts, not one per row. */
  alertsByDeviceId,
}) => {
  /* Ids compare as strings: device_id arrives as a number on the row while the alert
     ids are normalized to strings. */
  const alarmsFor = (deviceId) => alertsByDeviceId?.get(String(deviceId)) || null;
  const activeColumns = (columnDefs || COLUMN_DEFS).filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      if (columnKey === 'resident_name') {
        const residents = row.residents || [];
        if (residents.length === 0) {
          values.add('(empty)');
        } else {
          residents.forEach((r) => {
            const name = r.resident_name;
            if (name) values.add(name);
          });
        }
      } else {
        const val = row[columnKey];
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      }
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const showCheckbox = editRights || deleteRights;
  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.device_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'device_name': {
        /* The marker rides the name column rather than getting one of its own:
           it is where the eye lands, and an extra column would cost width on a
           table that already scrolls sideways. */
        const alarms = alarmsFor(row.device_id);
        /* The shared rule (B8): network coordinators, repeaters and locators are
           Inovonics system types whose profile page renders empty, so they are
           plain text here rather than a link to a blank page. */
        const deviceLink = getDeviceProfileLink(row);
        return (
          <>
            {alarms ? (
              <AlertBellPopover alerts={alarms} tzOffset={tzOffset} scope="device" />
            ) : null}
            {deviceLink ? (
            <Link
              to={deviceLink}
              className="dp-device-link"
            >
              {row.device_name}
            </Link>
          ) : (
            <span
              className="dp-cell-text"
              style={{ fontWeight: 'var(--font-weight-semibold)' }}
            >
              {row.device_name}
            </span>
          )}
            {alarms ? (
              <span className="pp-alarm-chip">
                <span className="pp-alarm-chip__dot" aria-hidden="true" />
                {alarms.length > 1 ? `${alarms.length} in alarm` : 'In alarm'}
              </span>
            ) : null}
          </>
        );
      }

      case 'residents': {
        const residents = row.residents || [];
        return residents.length > 0 ? (
          <div className="dp-resident-list">
            {residents.map((r, i) => (
              <Link
                key={r.resident_id || i}
                to={'/community/residents/' + r.resident_id}
                className="dp-resident-badge"
              >
                {r.resident_name}
              </Link>
            ))}
          </div>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );
      }

      case 'area_name':
        return row.area_id ? (
          <Link to={'/community/areas/' + row.area_id} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        ) : (
          <span className="dp-cell-text">{row.area_name || '—'}</span>
        );

      case 'is_active': {
        if (!row.is_active) {
          return (
            <span className="dp-status dp-status--inactive">
              <span className="dp-status__dot" aria-hidden="true" />
              Unassigned
            </span>
          );
        }
        /* Manufacturers that never heartbeat can't be judged on last_active —
           see STALE_EXEMPT_MFG. Skipping the test rather than faking a timestamp
           keeps the reason visible where the badge is chosen. */
        /* Suppression outranks staleness: it is a deliberate state someone set and
           can undo, while staleness is inferred from a timestamp. When both are
           true, the suppression is the thing staff need to know. */
        const muted = showsMuted(row.is_active, row.away_until);
        const stale = !muted
          && !skipsStaleCheck(row.manufacture_id, STALE_EXEMPT_MFG)
          && (row.last_active
            ? (Date.now() - campusLocalToMs(row.last_active, tzOffset)) > 30 * 3600000
            : true);
        const variant = muted ? 'dp-status--muted' : (stale ? 'dp-status--warning' : 'dp-status--active');
        const statusMsg = muted
          ? MUTED_BADGE.title
          : (stale ? 'Device may be stale \u2014 test the device to ensure functionality' : undefined);
        return (
          <StatusPopover message={statusMsg}>
            <span className={`dp-status ${variant}`}>
              <span className="dp-status__dot" aria-hidden="true" />
              {muted ? MUTED_BADGE.label : 'Active'}
            </span>
          </StatusPopover>
        );
      }

      case 'last_alarm':
        return row.last_alarm ? (
          <StatusPopover message={fullDateTime(row.last_alarm)}>
            <span className={`dp-cell-text dp-cell-text--secondary${row?.last_alarm ? " has-popover" : ""}`}>
              {relativeTimeFromCampusLocal(row.last_alarm, tzOffset)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'last_active':
        return row.last_active ? (
          <StatusPopover message={fullDateTime(row.last_active)}>
            <span className={`dp-cell-text dp-cell-text--secondary${row?.last_active ? " has-popover" : ""}`}>
              {relativeTimeFromCampusLocal(row.last_active, tzOffset)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'low_battery':
        return row.low_battery === 1 ? (
          <StatusPopover message="Battery Low">
            <span className="dp-status-icon dp-status-icon--error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
          </StatusPopover>
        ) : (
          <StatusPopover message="Battery Ok">
            <span className="dp-status-icon dp-status-icon--ok">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </span>
          </StatusPopover>
        );

      case 'tamper':
        return row.tamper === 1 ? (
          <StatusPopover message="Device Open">
            <span className="dp-status-icon dp-status-icon--error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
          </StatusPopover>
        ) : (
          <StatusPopover message="Device Secured">
            <span className="dp-status-icon dp-status-icon--ok">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </span>
          </StatusPopover>
        );

      case 'offline':
        return row.offline === 1 ? (
          <StatusPopover message="Device Offline">
            <span className="dp-status-icon dp-status-icon--error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
                <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0122.56 9" />
                <path d="M1.42 9a15.91 15.91 0 014.7-2.88" />
                <path d="M8.53 16.11a6 6 0 016.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </span>
          </StatusPopover>
        ) : (
          <StatusPopover message="Device Online">
            <span className="dp-status-icon dp-status-icon--ok">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1.42 9a16 16 0 0121.16 0" />
                <path d="M5 12.55a11 11 0 0114.08 0" />
                <path d="M8.53 16.11a6 6 0 016.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </span>
          </StatusPopover>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            {showCheckbox ? (
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all devices"
                />
              </th>
            ) : null}

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.device_id);
            return (
              <tr
                key={row.device_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}${alarmsFor(row.device_id) ? ' has-alarm' : ''}`}
              >
                {/* Checkbox */}
                {showCheckbox ? (
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.device_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${row.device_name}`}
                    />
                  </td>
                ) : null}

                {/* Row actions kebab */}
                <td className="dp-td dp-td--actions">
                  <DeviceRowActions
                    device={row}
                    editRights={editRights}
                    deleteRights={deleteRights}
                    onUnassignDevice={onUnassignDevice}
                    onDeleteDevice={onDeleteDevice}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * DeviceRowActions — kebab menu for per-row device actions.
 */
const DeviceRowActions = ({
  device,
  editRights,
  deleteRights,
  onUnassignDevice,
  onDeleteDevice,
}) => {
  const showUnassign = editRights && device.is_active;
  const showDelete = deleteRights;
  // Only devices with a profile page (the shared rule, as the name link uses).
  const profileLink = getDeviceProfileLink(device);
  const showViewProfile = Boolean(profileLink);

  // Don't render menu trigger if no actions are available
  if (!showUnassign && !showDelete && !showViewProfile) {
    return null;
  }

  return (
    <RowActionsMenu label={`Actions for ${device.device_name}`} classPrefix="dp">
      {(close) => (
        <>
          {/* View Profile — only for devices with a profile page */}
          {showViewProfile ? (
            <Link
              to={profileLink}
              className="dp-row-actions__item"
              style={{ textDecoration: 'none' }}
              onClick={() => close()}
            >
              <svg className="dp-icon-md dp-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              View Profile
            </Link>
          ) : null}

          {/* Unassign Device — only if editRights and device is active */}
          {showUnassign ? (
            <button
              type="button"
              onClick={() => {
                onUnassignDevice(device);
                close();
              }}
              className="dp-row-actions__item"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7.5 10.5h.008v.008H7.5V10.5zm0 0a4.5 4.5 0 019 0m-9 0H3.75m13.5 0h.008v.008h-.008V10.5zm0 0H21.75m-3.75 0a4.5 4.5 0 00-9 0M12 3v1.5M12 19.5V21M4.22 4.22l1.06 1.06m13.44 13.44l1.06 1.06M1.5 12h1.5m16.5 0h1.5M4.22 19.78l1.06-1.06M18.72 5.28l1.06-1.06"
                />
              </svg>
              Unassign Device
            </button>
          ) : null}

          {/* Delete Device — only if deleteRights */}
          {showDelete ? (
            <button
              type="button"
              onClick={() => {
                onDeleteDevice(device);
                close();
              }}
              className="dp-row-actions__item dp-row-actions__item--danger"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
              Delete Device
            </button>
          ) : null}
        </>
      )}
    </RowActionsMenu>
  );
};

export default DevicesTable;
